import React, {useState} from 'react';
import z from 'zod';

import Form from '../common/Form';

import useUser from '../../hooks/useUser';
import {getServiceCategories} from '../../helpers/selectOptions';
import {
  useCreateSessionTypeMutation, 
  useUpdateSessionTypeMutation
} from '../../redux/services/sessionTypeApi';

const sections = () => ([
  {
    name: 'general',
    label: '🗓️ General',
  },
  {
    name: 'evaluation',
    label: '👋 Evaluation Session',
    prerequisites: ['general'],
  },
  {
    name: 'followup',
    label: '🔁 Follow-up Session',
    prerequisites: ['general'],
  },
  {
    name: 'options',
    label: '⚙️ More Options',
    prerequisites: ['general'],
  },
]);

const getFields = ({
  serviceCategoriesUsed,
  setServiceCategory
}) => [
  {
    name: 'service_category',
    section: 'general',
    label: 'Service Category',
    displayName: 'Service Category',
    type: 'select',
    placeholder: 'Select a service category',
    defaultValue: null,
    required: true,
    options: getServiceCategories().filter(c => !serviceCategoriesUsed.includes(c.value)),
    maxWidth: '250px',
    grid: {xs: 12, md: 6},
    notEditable: true,
    onChange: (e, value) => setServiceCategory(value),
    parser: (e, value) => ({
      service_category: value, 
      is_physical_therapy: value === 'PHYSICAL_THERAPY',
    }),
  },
  {
    name: 'description',
    section: 'general',
    label: 'Description',
    displayName: 'Description',
    type: 'textarea',
    placeholder: 'Add a description...',
    defaultValue: '',
    required: false,
    appendDivider: false,
    validate: str => true,
    grid: {xs: 12, md: 12},
  },
  {
    name: 'eval_duration_minutes',
    section: 'evaluation',
    label: 'Duration (minutes)',
    displayName: 'Session duration (minutes)',
    type: 'number',
    placeholder: '30',
    defaultValue: '30',
    InputProps: { inputProps: { type: 'text', min: '0', max: '999', step: '1', maxlength: '3' } },
    required: true,
    requiredForScheduling: true,
    hideIfAvailabilityHidden: false,
    validate: str => !!str,
    grid: {xs: 6, md: 3},
  },
  {
    name: 'eval_hourly_rate',
    section: 'evaluation',
    label: 'Session rate / hour',
    displayName: 'Session hourly rate',
    type: 'currency',
    placeholder: '120.00',
    defaultValue: '',
    InputProps: { inputProps: { type: 'text', min: '0', max: '999', step: '0.01', maxlength: '6' } },
    required: true,
    validate: str => !!str || ['0', 0].includes(str),
    appendDivider: false,
    grid: {xs: 6, md: 3},
  },
  {
    name: 'duration_minutes',
    section: 'followup',
    label: 'Duration (minutes)',
    displayName: 'Session duration (minutes)',
    type: 'number',
    placeholder: '30',
    defaultValue: '30',
    InputProps: { inputProps: { type: 'text', min: '0', max: '999', step: '1', maxlength: '3' } },
    required: true,
    requiredForScheduling: true,
    hideIfAvailabilityHidden: false,
    validate: str => !!str,
    grid: {xs: 6, md: 3},
  },
  {
    name: 'hourly_rate',
    section: 'followup',
    label: 'Session rate / hour',
    displayName: 'Session hourly rate',
    type: 'currency',
    placeholder: '120.00',
    defaultValue: '',
    InputProps: { inputProps: { type: 'text', min: '0', max: '999', step: '0.01', maxlength: '6' } },
    required: true,
    validate: str => !!str || ['0', 0].includes(str),
    appendDivider: false,
    grid: {xs: 6, md: 3},
  },
  {
    name: 'is_physical_therapy',
    section: 'options',
    label: 'Restore or Optimize',
    displayName: 'Session Category',
    type: 'choice',
    helperText: <span><strong>Restore</strong>: Recovery-focused. <strong>Optimize</strong>: Performance-focused.</span>,
    options: [
      { value: true, label: 'Restore' },
      { value: false, label: 'Optimize' },
    ],
    defaultValue: !serviceCategoriesUsed.includes('PHYSICAL_THERAPY'),
    required: false,
    appendDivider: false,
    validate: () => true,
    grid: {xs: 12, md: 6},
  },
  // {
  //   name: 'instructions',
  //   section: 'options',
  //   label: 'Instructions',
  //   displayName: 'Instructions',
  //   type: 'textarea',
  //   placeholder: 'Add instructions for the patient (For example: parking, building entry, etc.)...',
  //   defaultValue: '',
  //   required: false,
  //   appendDivider: false,
  //   validate: str => !!str,
  //   grid: {xs: 12, md: 12},
  // },
  // {
  //   name: 'session_meeting_url',
  //   section: 'options',
  //   label: 'Session link',
  //   displayName: 'Session meeting link',
  //   type: 'text',
  //   helperText: 'Add a URL for virtual meetings (starting with https://)',
  //   placeholder: 'https://meet.google.com/abc-defg-hij',
  //   appendDivider: false,
  //   required: false,
  //   hideIfAvailabilityHidden: false,
  //   virtualOnly: true,
  //   validate: str => !!str && /^https:\/\//.test(str),
  //   schema: z.string().nullable().optional(),
  // },
];


const getDefaultValues = ({
  sessionType, 
  provider, 
  serviceCategoriesUsed
}) => ({
  service_category: (
    sessionType?.service_category 
    // || getServiceCategories().find(c => !serviceCategoriesUsed.includes(c.value))?.value
    || undefined
  ),
  description: sessionType?.description || '',
  eval_duration_minutes: sessionType?.eval_duration_minutes || 60,
  eval_hourly_rate: sessionType?.eval_hourly_rate || provider?.rate || 120,
  duration_minutes: sessionType?.duration_minutes || 60,
  hourly_rate: sessionType?.hourly_rate || provider?.rate || 120,
  is_physical_therapy: (
    sessionType?.is_physical_therapy !== undefined
    ? sessionType.is_physical_therapy
    : (
      !serviceCategoriesUsed.includes('PHYSICAL_THERAPY')
      || false
    )
  ),
  // session_meeting_url: sessionType?.session_meeting_url || null,
});

const serialize = data => ({
  ...data,
});

const handleSubmit = ({
  isEditing, 
  providerId, 
  send, 
  onSuccess, 
  sessionTypeId
} = {}) => async formData => {
  const body = (
    isEditing ? {
      id: sessionTypeId,
      body: formData,
    }
    : {
      ...formData, 
      // session_type_version: '2.0',
      provider_id: providerId,
    }
  );
  const request = await send(body);
  if (!request?.error && onSuccess) onSuccess(request);
  // return a failed promise if there was an error
  if (request?.error) return Promise.reject(new Error(request?.error?.data?.errors?.[0]));
  return Promise.resolve(request);
};

const SessionTypeV2Form = ({
  sessionType = null,
  isEditing = false,
  backgroundColor = '#ffffff',
  serviceCategoriesUsed = [],
  onSuccess = e => console.log('onSuccess', e),
}) => {
  const [serviceCategory, setServiceCategory] = useState(sessionType?.service_category);
  const user = useUser();
  const [post] = useCreateSessionTypeMutation();
  const [put] = useUpdateSessionTypeMutation();
  if (!user?.provider) return null;
  const fields = getFields({serviceCategoriesUsed, setServiceCategory});
  const send = isEditing ? put : post;
  const onSubmit = handleSubmit({
    isEditing,
    sessionTypeId: sessionType?.id,
    send,
    onSuccess,
    providerId: user.provider.id,
  });
  return (
    <Form
      key={sessionType?.id || 'new'}
      defaultValues={getDefaultValues({
        sessionType, 
        provider: user.provider,
        serviceCategoriesUsed,
      })}
      sections={sections()
        .filter(s => // hide fields for THIRTY_FOR_THIRTY service category
        serviceCategory !== 'THIRTY_FOR_THIRTY'
        || !['evaluation', 'followup'].includes(s.name))
      }
      fields={
        fields
          .filter(f => isEditing ? !f?.notEditable : true)
          .filter(f =>
            // hide fields for FREE_EVAL category
            serviceCategory !== 'FREE_EVAL'
            || ![
              'eval_hourly_rate', 
              'hourly_rate'
            ].includes(f.name)
          )
      }
      backgroundColor={backgroundColor}
      serialize={serialize}
      onSubmit={onSubmit}
    />
  );
};

export default SessionTypeV2Form;